import { baseTheme } from './base';
import type { ThemeDefinition } from '../types';

export const darkTheme: ThemeDefinition = {
  ...baseTheme,
  colors: {
    ...baseTheme.colors,

    // Background
    bg: 'black',
    'bg.base': 'gray.950',
    'bg.sunken': 'gray.900',
    'bg.raised': 'gray.950',
    'bg.floating': 'gray.800',
    'bg.inverted': 'white',
    'bg.panel': 'gray.900',
    'bg.overlay': 'gray.900',
    'bg.error': 'red.900',
    'bg.error.solid': 'red.600',
    'bg.warning': 'goldenGlow.900',
    'bg.warning.solid': 'goldenGlow.300',
    'bg.success': 'green.900',
    'bg.success.solid': 'green.400',
    'bg.info': 'blue.900',
    'bg.info.solid': 'blue.500',
    'bg.info.inverted': 'blue.500',
    'bg.accent': 'cyan.900',
    'bg.accent.solid': 'cyan.600',

    // Text
    fg: 'gray.50',
    'fg.constant': 'gray.950',
    'fg.disabled': 'gray.600',
    'fg.muted': 'gray.400',
    'fg.muted.constant': 'gray.600',
    'fg.inverted': 'gray.950',
    'fg.inverted.constant': 'gray.50',
    'fg.error': 'red.400',
    'fg.warning': 'goldenGlow.400',
    'fg.success': 'green.400',
    'fg.accent': 'cyan.400',

    // Border
    border: 'gray.800',
    'border.subtle': 'gray.900',
    'border.inverted': 'gray.200',
    'border.error': 'red.400',
    'border.warning': 'goldenGlow.400',
    'border.success': 'green.400',
    'border.info': 'blue.400',
    'border.accent': 'cyan.400',

    // Card
    'card.bg.emphasized': 'gray.800',
    'card.bg.emphasized.hover': 'gray.900',
    'card.emphasized.stripes': 'whiteAlpha.400',
    'card.bg.emphasized.infobox': 'whiteAlpha.300',
    'card.bg.muted': 'gray.800',
    'card.bg.muted.hover': 'gray.900',
    'card.bg.delay': 'goldenGlow.900',
    'card.bg.delay.hover': 'goldenGlow.950',
    'card.active.stripes': 'whiteAlpha.400',
    'card.bg.ahead': 'titanWhite.900',
    'card.bg.ahead.hover': 'titanWhite.950',
    'card.bg.ontime': 'teal.800',
    'card.bg.ontime.hover': 'teal.900',
    'card.bg.subtle': 'gray.900',
    'card.bg.subtle.hover': 'gray.950',

    // Button
    'button.bg.primary': 'cyan.600',
    'button.bg.primary.hover': 'cyan.700',
    'button.bg.primary.active': 'cyan.800',
    'button.bg.primary.subtle': 'cyan.700',
    'button.bg.primary.subtle.hover': 'cyan.800',
    'button.bg.primary.subtle.active': 'cyan.900',
    'button.bg.secondary': 'gray.700',
    'button.bg.secondary.hover': 'gray.800',
    'button.bg.secondary.active': 'gray.900',
    'button.bg.ghost': 'transparent',
    'button.bg.ghost.hover': 'whiteAlpha.200',
    'button.bg.ghost.active': 'whiteAlpha.300',
    'button.bg.critical': 'red.600',
    'button.bg.critical.hover': 'red.700',
    'button.bg.critical.active': 'red.800',
    'button.bg.critical.subtle': 'red.700',
    'button.bg.critical.subtle.hover': 'red.800',
    'button.bg.critical.subtle.active': 'red.900',
    'button.bg.warning.subtle': 'goldenGlow.800',
    'button.bg.warning.subtle.hover': 'goldenGlow.900',
    'button.bg.warning.subtle.active': 'goldenGlow.950',
    'button.bg.ai': 'titanWhite.600',
    'button.bg.ai.hover': 'titanWhite.700',
    'button.bg.ai.active': 'titanWhite.800',

    // Tag
    'tag.secondary.bg.default': 'gray.800',
    'tag.secondary.bg.hover': 'gray.700',
    'tag.secondary.bg.active': 'gray.600',
    'tag.secondary.solid.bg.default': 'gray.600',
    'tag.secondary.solid.bg.hover': 'gray.700',
    'tag.secondary.solid.bg.active': 'gray.800',
    'tag.primary.bg.default': 'cyan.900',
    'tag.primary.bg.hover': 'cyan.800',
    'tag.primary.bg.active': 'cyan.700',
    'tag.primary.solid.bg.default': 'cyan.600',
    'tag.primary.solid.bg.hover': 'cyan.700',
    'tag.primary.solid.bg.active': 'cyan.800',
    'tag.warning.bg.default': 'goldenGlow.950',
    'tag.warning.bg.hover': 'goldenGlow.900',
    'tag.warning.bg.selected': 'goldenGlow.800',
    'tag.warning.solid.bg.default': 'goldenGlow.600',
    'tag.warning.solid.bg.hover': 'goldenGlow.700',
    'tag.warning.solid.bg.active': 'goldenGlow.800',
    'tag.critical.bg.default': 'red.900',
    'tag.critical.bg.hover': 'red.800',
    'tag.critical.bg.active': 'red.700',
    'tag.critical.solid.bg.default': 'red.600',
    'tag.critical.solid.bg.hover': 'red.700',
    'tag.critical.solid.bg.active': 'red.800',
    'tag.ai.bg.default': 'titanWhite.950',
    'tag.ai.bg.hover': 'titanWhite.900',
    'tag.ai.bg.active': 'titanWhite.800',
    'tag.ai.solid.bg.default': 'titanWhite.600',
    'tag.ai.solid.bg.hover': 'titanWhite.700',
    'tag.ai.solid.bg.active': 'titanWhite.800',

    // Scrollbar
    'scrollbar.track': 'transparent',
    'scrollbar.thumb': 'gray.700',

    // Input
    'input.bg': 'gray.950',
    'input.bg.hover': 'gray.900',
    'input.bg.focus': 'gray.950',
    'input.bg.disabled': 'gray.800',
    'input.border': 'gray.600',
    'input.border.hover': 'gray.600',
    'input.border.focus': 'cyan.400',
    'input.border.error': 'red.400',
    'input.border.success': 'green.400',
    'input.text': 'gray.50',
    'input.text.placeholder': 'gray.600',
    'input.text.disabled': 'gray.600',
  },
};
